import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { WalletContext } from '../context/WalletContex';
import TopBar    from '../components/layout/TopBar';
import Button    from '../components/ui/Button';
import Input     from '../components/ui/Input';
import Modal     from '../components/ui/Modal';
import apiClient from '../utils/apiClient';
import { toast } from 'react-toastify';
import { HiArrowsUpDown, HiCheckCircle, HiExclamationTriangle } from 'react-icons/hi2';

// Always use toFixed — never let JS produce scientific notation (e.g. 7.5e-7)
const fmtCoin = (num, dp) =>
  (num === null || num === undefined || isNaN(Number(num))) ? '—' : Number(num).toFixed(dp);

// Floor to dp decimal places so Max button never produces an amount that exceeds the balance
const floorToDp = (val, dp) => {
  const factor = Math.pow(10, dp);
  return Math.floor(Number(val) * factor) / factor;
};

// Coin IDs match backend ledger keys (usdt, not usdttrc20)
// dp must be the full on-chain precision — rounding causes "insufficient balance" on Max
const COINS = [
  { id: 'btc',  label: 'Bitcoin',      symbol: 'BTC',  network: 'Bitcoin Network',  cls: 'btc',  icon: '₿', dp: 8 },
  { id: 'usdt', label: 'USDT (TRC20)', symbol: 'USDT', network: 'Tron Network',      cls: 'usdt', icon: '$', dp: 6 },
  { id: 'trx',  label: 'TRON',         symbol: 'TRX',  network: 'Tron Network',      cls: 'trx',  icon: 'T', dp: 6 },
  { id: 'bnb',  label: 'BNB',          symbol: 'BNB',  network: 'BNB Smart Chain',   cls: 'eth',  icon: 'B', dp: 8 },
];

export default function Swap() {
  const { wallet } = useContext(WalletContext);

  const [fromId, setFromId]           = useState('btc');
  const [toId, setToId]               = useState('usdt');
  const [amount, setAmount]           = useState('');
  const [balances, setBalances]       = useState({});
  const [balLoading, setBalLoading]   = useState(false);
  const [quote, setQuote]             = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [swapping, setSwapping]       = useState(false);
  const [result, setResult]           = useState(null); // success result
  const userPickedFrom = useRef(false);

  const fromCoin = COINS.find(c => c.id === fromId);
  const toCoin   = COINS.find(c => c.id === toId);
  const toCoins  = COINS.filter(c => c.id !== fromId);

  // Fetch all custodial balances at once
  const fetchBalances = useCallback(async () => {
    if (!wallet?.email) return;
    setBalLoading(true);
    try {
      const { data } = await apiClient.get(`/wallet/balances?email=${encodeURIComponent(wallet.email)}`);
      setBalances(data);
    } catch { /* silent */ }
    finally { setBalLoading(false); }
  }, [wallet?.email]);

  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  // Auto-select highest value coin as From on first load
  useEffect(() => {
    if (userPickedFrom.current) return;
    if (!Object.keys(balances).length) return;
    // Simple heuristic: USDT > BTC > BNB > TRX (most liquidity likely in USDT)
    const order = ['usdt', 'btc', 'bnb', 'trx'];
    const best = order.find(id => (balances[id] ?? 0) > 0);
    if (best && best !== fromId) {
      setFromId(best);
      setToId(COINS.find(c => c.id !== best)?.id ?? 'btc');
    }
  }, [balances]);

  // Validate amount against balance
  useEffect(() => {
    if (!amount) { setAmountError(''); return; }
    const num = parseFloat(amount);
    const bal = balances[fromId] ?? 0;
    if (isNaN(num) || num <= 0) { setAmountError('Enter a valid amount'); return; }
    if (num > bal) {
      setAmountError(`Insufficient balance (you have ${bal.toFixed(fromCoin?.dp ?? 6)} ${fromCoin?.symbol})`);
      return;
    }
    setAmountError('');
  }, [amount, balances, fromId, fromCoin]);

  // Debounced quote fetch
  useEffect(() => {
    setQuote(null);
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 || amountError) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const { data } = await apiClient.get(
          `/swap/quote?fromCoin=${fromId}&toCoin=${toId}&fromAmount=${amount}&email=${encodeURIComponent(wallet?.email ?? '')}`
        );
        if (!cancelled) setQuote(data);
      } catch (err) {
        if (!cancelled) setAmountError(err.response?.data?.error || 'Could not get quote');
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [amount, fromId, toId, amountError, wallet?.email]);

  const handleFromChange = (newFrom) => {
    userPickedFrom.current = true;
    setFromId(newFrom);
    if (toId === newFrom) setToId(COINS.find(c => c.id !== newFrom)?.id ?? 'usdt');
    setAmount(''); setQuote(null); setAmountError('');
  };

  const handleToChange = (newTo) => {
    setToId(newTo);
    setQuote(null);
  };

  const flipCoins = () => {
    userPickedFrom.current = true;
    setFromId(toId); setToId(fromId);
    setAmount(''); setQuote(null); setAmountError('');
  };

  const onSwap = async () => {
    setSwapping(true);
    try {
      const { data } = await apiClient.post('/swap/execute', {
        email: wallet?.email,
        fromCoin: fromId,
        toCoin:   toId,
        fromAmount: parseFloat(amount),
      });
      setResult(data);
      setConfirmOpen(false);
      fetchBalances(); // refresh balances
    } catch (err) {
      toast.error(err.response?.data?.error || 'Swap failed');
    } finally {
      setSwapping(false);
    }
  };

  // Success screen
  if (result) {
    return (
      <div>
        <TopBar title="Swap Complete" />
        <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#110E30', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)', padding: 28, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: 22, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <HiCheckCircle size={32} style={{ color: '#10B981' }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#10B981', marginBottom: 6 }}>Swap Successful</div>
            <div style={{ fontSize: 13, color: '#8B8FA8' }}>Instant — no waiting required</div>
          </div>

          <div style={{ background: '#1A1748', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(() => {
              const fC = COINS.find(c => c.id === result.fromCoin);
              const tC = COINS.find(c => c.id === result.toCoin);
              return (<>
                <ResultRow label="You swapped"  value={`${fmtCoin(result.fromAmount, fC?.dp)} ${fC?.symbol}`} />
                <ResultRow label="You received" value={`${fmtCoin(result.toAmount, tC?.dp)} ${tC?.symbol}`} bold />
                <ResultRow label="Rate"         value={`1 ${fC?.symbol} = ${fmtCoin(result.rate, tC?.dp)} ${tC?.symbol}`} />
                <ResultRow label="Platform fee" value={`${fmtCoin(result.feeAmount, fC?.dp)} ${fC?.symbol} (${(result.feeRate*100).toFixed(2)}%)`} muted />
              </>);
            })()}
          </div>

          <Button variant="primary" size="lg" full onClick={() => { setResult(null); setAmount(''); setQuote(null); }}>
            Swap Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Swap" back />
      <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* From */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#8B8FA8', textTransform: 'uppercase', letterSpacing: '.05em' }}>From</span>
            {balLoading && <span style={{ fontSize: 11, color: '#4B5170' }}>Loading balances…</span>}
          </div>
          <div style={{ background: '#1A1748', borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            {COINS.map(coin => {
              const bal = balances[coin.id] ?? 0;
              return (
                <button key={coin.id} onClick={() => handleFromChange(coin.id)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px',
                  background: fromId === coin.id ? 'rgba(124,92,246,0.12)' : 'transparent',
                  border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer',
                }}>
                  <div className={`coin-icon ${coin.cls}`} style={{ width: 34, height: 34, borderRadius: 10, fontSize: 14, flexShrink: 0 }}>{coin.icon}</div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{coin.label}</div>
                    <div style={{ fontSize: 11, color: '#4B5170' }}>{coin.network}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: bal > 0 ? '#fff' : '#4B5170' }}>{fmtCoin(bal, coin.dp)} {coin.symbol}</div>
                  </div>
                  {fromId === coin.id && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#7C5CF6', flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Flip button */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button onClick={flipCoins} style={{
            width: 40, height: 40, borderRadius: 12,
            background: 'rgba(124,92,246,0.12)', border: '1px solid rgba(124,92,246,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#7C5CF6',
          }}>
            <HiArrowsUpDown size={18} />
          </button>
        </div>

        {/* To */}
        <div>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#8B8FA8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>To</p>
          <div style={{ background: '#1A1748', borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            {toCoins.map(coin => (
              <button key={coin.id} onClick={() => handleToChange(coin.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px',
                background: toId === coin.id ? 'rgba(16,185,129,0.08)' : 'transparent',
                border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer',
              }}>
                <div className={`coin-icon ${coin.cls}`} style={{ width: 34, height: 34, borderRadius: 10, fontSize: 14, flexShrink: 0 }}>{coin.icon}</div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{coin.label}</div>
                  <div style={{ fontSize: 11, color: '#4B5170' }}>{coin.network}</div>
                </div>
                {toId === coin.id && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981', flexShrink: 0 }} />}
              </button>
            ))}
          </div>
        </div>

        {/* Amount */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#8B8FA8' }}>Amount ({fromCoin?.symbol})</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#4B5170' }}>
                Balance: {fmtCoin(balances[fromId] ?? 0, fromCoin?.dp ?? 6)} {fromCoin?.symbol}
              </span>
              <button
                onClick={() => {
                  const bal = balances[fromId] ?? 0;
                  if (bal <= 0) return;
                  setAmount(floorToDp(bal, fromCoin?.dp ?? 8).toFixed(fromCoin?.dp ?? 8));
                }}
                style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: 'rgba(124,92,246,0.15)', border: '1px solid rgba(124,92,246,0.3)', color: '#7C5CF6' }}>
                Max
              </button>
            </div>
          </div>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={e => {
              const clean = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
              setAmount(clean);
            }}
            error={amountError}
          />
        </div>

        {/* Quote */}
        {quoteLoading && (
          <div style={{ textAlign: 'center', color: '#4B5170', fontSize: 13 }}>Getting best rate…</div>
        )}
        {quote && !quoteLoading && (
          <div style={{ background: '#1A1748', borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <QuoteRow label="Rate"         value={`1 ${fromCoin?.symbol} = ${fmtCoin(quote.rate, toCoin?.dp)} ${toCoin?.symbol}`} />
            <QuoteRow label="Platform fee" value={`${fmtCoin(quote.feeAmount, fromCoin?.dp)} ${fromCoin?.symbol} (${(quote.feeRate * 100).toFixed(2)}%)`} muted />
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '2px 0' }} />
            <QuoteRow label="You receive"  value={`${fmtCoin(quote.toAmount, toCoin?.dp)} ${toCoin?.symbol}`} bold />
            <div style={{ fontSize: 11, color: '#4B5170', marginTop: 2 }}>
              ≈ ${(quote.toAmount * quote.toPriceUsd).toFixed(2)} USD · Rate valid for ~30 seconds
            </div>
          </div>
        )}

        {quote && (
          <div className="warning-banner">
            <HiExclamationTriangle size={15} style={{ color: '#F59E0B', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#F59E0B', lineHeight: 1.5 }}>
              Rate is locked at execution time. Swap is instant and irreversible.
            </span>
          </div>
        )}

        <Button
          variant="primary" size="lg" full
          disabled={!quote || !amount || !!amountError}
          onClick={() => setConfirmOpen(true)}>
          Review Swap
        </Button>
      </div>

      {/* Confirm modal */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Confirm Swap">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <ConfirmRow label="Sending"      value={`${fmtCoin(amount, fromCoin?.dp)} ${fromCoin?.symbol}`} />
          <ConfirmRow label="Receiving"    value={`≈ ${fmtCoin(quote?.toAmount, toCoin?.dp)} ${toCoin?.symbol}`} />
          <ConfirmRow label="Platform fee" value={`${fmtCoin(quote?.feeAmount, fromCoin?.dp)} ${fromCoin?.symbol} (${((quote?.feeRate ?? 0) * 100).toFixed(2)}%)`} />
          <ConfirmRow label="Rate"         value={`1 ${fromCoin?.symbol} = ${fmtCoin(quote?.rate, toCoin?.dp)} ${toCoin?.symbol}`} />
          <div className="warning-banner">
            <HiExclamationTriangle size={16} style={{ color: '#F59E0B', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#F59E0B', lineHeight: 1.5 }}>
              This swap is instant and cannot be undone. The final rate is locked at execution.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" size="md" full onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="primary"   size="md" full onClick={onSwap} loading={swapping}>Confirm Swap</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function QuoteRow({ label, value, bold, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, color: '#4B5170' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: muted ? '#4B5170' : bold ? '#fff' : '#8B8FA8' }}>{value}</span>
    </div>
  );
}

function ResultRow({ label, value, bold, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, color: '#4B5170' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: muted ? '#4B5170' : bold ? '#10B981' : '#8B8FA8' }}>{value}</span>
    </div>
  );
}

function ConfirmRow({ label, value }) {
  return (
    <div style={{ background: '#1A1748', borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#4B5170', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{value}</div>
    </div>
  );
}
